import { inject } from '@angular/core';
import {
    patchState,
    signalStore,
    withMethods,
    withState,
} from '@ngrx/signals';

import { initialAuthState } from './auth.state';
import { AuthService } from './auth.service';
import { LoggerService } from '../../logging/logger.service';


/**
 * This store manages the authentication state using NgRx Signals.
 * It provides methods to interact with the AuthService and update the state accordingly.
 * The store is initialized with the initial authentication state and provides methods to handle authentication-related actions.
    AuthStore
        ↓
    AuthService
        ↓
    AWS Amplify -> Can be swapped with another authentication service in the future if needed.
 */

export const AuthStore = signalStore(
    { providedIn: "root" },
    withState(initialAuthState),
    withMethods((store) => {
        const authService = inject(AuthService);
        const logger = inject(LoggerService);
        
        const loadUser = async () => {
            patchState(store, {loadingLogging: true, error: null});
            try {
                logger.debug('AuthStore', 'Loading user information');
                const user = await authService.loadUser();
                patchState(store, {
                    username: user?.username ?? "",
                    role: user?.role?.toLowerCase() ?? "",
                    isAuthenticated: !!user,
                    loadingLogging: false,
                });
                if (user) {
                    logger.info('AuthStore', 'Authenticated user loaded successfully:', { user });
                } else {
                    logger.debug('AuthStore', 'No user found');
                }
            } catch (error) {
                patchState(store, {
                    ...initialAuthState,
                    error: (error as Error).message,
                });
                logger.error('AuthStore', 'Error loading user information', error);
            }
        };

        return{
            loadUser,

            async login() {
                logger.info('AuthStore', 'User login initiated');
                patchState(store, {loadingLogging: true, error: null});
                try {
                    await authService.login();
                    await loadUser();
                    logger.info('AuthStore', 'Login flow completed', { authenticated: store.isAuthenticated() });
                    patchState(store, {loadingLogging: false});
                } catch (error) {
                    logger.error('AuthStore', 'Login failed', error);
                    throw error;
                }
            },

            async logout() {
                logger.info('AuthStore', 'User logout initiated');
                patchState(store, {loadingLogging: true, error: null});
                try {
                    await authService.logout();
                    logger.info('AuthStore', 'User logged out successfully');
                    patchState(store, {...initialAuthState, loadingLogging: false});
                } catch (error) {
                    logger.error('AuthStore', 'Logout failed', error);
                    throw error;
                }
            }
        }
    })
)